import React, { useState, useEffect } from 'react';
import {
    Modal,
    NumberInput,
    Select,
    SelectItem,
    Toggle,
    InlineNotification,
} from '@carbon/react';
import axios from 'axios';
import { GatewayIp } from '../../../data/endpoints';
import { endpoints } from '../../../data/endpoints';

type DurationUnit = 'days' | 'hours' | 'minutes' | 'seconds';

interface ICleanupResult {
    watchfolderId: string;
    olderThan: string;
    deleteFiles: boolean;
    candidates: number;
    metadataRowsDeleted: number;
    filesDeleted: number;
    filesMissing: number;
    filesSkipped: number;
    fileDeleteFailures: number;
}

type Props = {
    open: boolean;
    watchfolderId: string | null;
    token: string;
    onClose: () => void;
};

const buildDuration = (value: number, unit: DurationUnit): string => {
    switch (unit) {
        case 'days': return `P${value}D`;
        case 'hours': return `PT${value}H`;
        case 'minutes': return `PT${value}M`;
        case 'seconds': return `PT${value}S`;
    }
};

const resultRows: { key: keyof ICleanupResult; label: string }[] = [
    { key: 'candidates', label: 'Candidates' },
    { key: 'metadataRowsDeleted', label: 'Metadata Rows Deleted' },
    { key: 'filesDeleted', label: 'Files Deleted' },
    { key: 'filesMissing', label: 'Files Missing' },
    { key: 'filesSkipped', label: 'Files Skipped' },
    { key: 'fileDeleteFailures', label: 'File Delete Failures' },
];

export const CleanupModal: React.FC<Props> = ({ open, watchfolderId, token, onClose }) => {
    const [value, setValue] = useState<number>(1);
    const [unit, setUnit] = useState<DurationUnit>('hours');
    const [deleteFiles, setDeleteFiles] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [result, setResult] = useState<ICleanupResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setValue(1);
            setUnit('hours');
            setDeleteFiles(false);
            setConfirming(false);
            setResult(null);
            setError(null);
            setLoading(false);
        }
    }, [open]);

    const handleSubmit = async () => {
        if (result) {
            onClose();
            return;
        }
        if (!confirming) {
            setConfirming(true);
            return;
        }
        if (!watchfolderId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post(
                GatewayIp + endpoints.watchfolder.metadataCleanup(watchfolderId),
                { olderThan: buildDuration(value, unit), deleteFiles },
                {
                    withCredentials: true,
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                }
            );
            setResult(response.data as ICleanupResult);
            setConfirming(false);
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'Cleanup failed');
            setConfirming(false);
        } finally {
            setLoading(false);
        }
    };

    const handleSecondary = () => {
        if (confirming) {
            setConfirming(false);
        } else {
            onClose();
        }
    };

    return (
        <Modal
            open={open}
            modalHeading="Clean Metadata"
            primaryButtonText={result ? 'Close' : loading ? 'Running…' : confirming ? 'Yes, Run Cleanup' : 'Run Cleanup'}
            primaryButtonDisabled={loading}
            secondaryButtonText={result ? undefined : confirming ? 'Back' : 'Cancel'}
            danger={!result}
            onRequestClose={onClose}
            onRequestSubmit={handleSubmit}
            onSecondarySubmit={handleSecondary}
            preventCloseOnClickOutside={true}
        >
            {confirming && !result ? (
                <div>
                    <p style={{ fontSize: 16, marginBottom: 12 }}>Are you certain?</p>
                    <p style={{ color: '#c6c6c6' }}>
                        This will delete metadata older than <strong>{value} {unit}</strong>.
                    </p>
                    {deleteFiles && (
                        <p style={{ color: '#fa4d56', marginTop: 10, fontWeight: 600 }}>
                            Files will be deleted from filesystem.
                        </p>
                    )}
                    {error && (
                        <div style={{ marginTop: 16 }}>
                            <InlineNotification kind="error" title="Error" subtitle={error} hideCloseButton />
                        </div>
                    )}
                </div>
            ) : !result ? (
                <>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                            <NumberInput
                                id="cleanup-value"
                                label="Older Than"
                                min={1}
                                value={value}
                                onChange={(_e: any, { value: v }: any) => setValue(Number(v))}
                            />
                        </div>
                        
                        <div style={{ flex: 1 }}>
                            <Select
                                id="cleanup-unit"
                                labelText="Unit"
                                value={unit}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setUnit(e.target.value as DurationUnit)}
                            >
                                <SelectItem value="days" text="Days" />
                                <SelectItem value="hours" text="Hours" />
                                <SelectItem value="minutes" text="Minutes" />
                                <SelectItem value="seconds" text="Seconds" />
                            </Select>
                        </div>
                    </div>
                    <Toggle
                        id="cleanup-delete-files"
                        labelText="Delete Files"
                        labelA="No"
                        labelB="Yes"
                        toggled={deleteFiles}
                        onToggle={(checked: boolean) => setDeleteFiles(checked)}
                    />
                </>
            ) : (
                <div>
                    <p style={{ marginBottom: 16 }}>
                        Cleanup completed — older than <strong>{result.olderThan}</strong>
                        {result.deleteFiles ? ', files deleted' : ''}.
                    </p>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            {resultRows.map(({ key, label }) => (
                                <tr key={key} style={{ borderBottom: '1px solid #393939' }}>
                                    <td style={{ padding: '8px 4px', color: '#c6c6c6' }}>{label}</td>
                                    <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600 }}>
                                        {result[key] as number}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
};
